// ─── SyncedListEditor — pure helpers ─────────────────────────────────────────
// Pure functions used by the SyncedListEditor component in AccountPage.jsx.
// Stateless, no React dependency — so they can be unit-tested in plain Node.
// All array mutations return new arrays (never mutate in place); React state
// updates rely on referential inequality.
//
// keyField parameter (Sprint 3.5):
// Each list-manipulating function accepts an optional `keyField` argument
// naming which property identifies items. Defaults to 'key' to preserve
// dimensions/metrics behaviour. Operator vocabulary entries use keyField='wire'
// since their shape is { display, wire } and `wire` is the natural identifier.

/**
 * Auto-derive a human-readable label from a snake_case / kebab-case key.
 * Examples:
 *   'date'           → 'Date'
 *   'app_bundle_id'  → 'App Bundle Id'
 *   'foo-bar'        → 'Foo Bar'
 *   'fooBar'         → 'FooBar' (camelCase passes through as-is)
 *   ''               → ''
 */
export function autoLabel(k) {
  if (!k || typeof k !== 'string') return '';
  return k.split(/[_\-\s]+/).filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/**
 * Default item factory for added entries. Returns { key, label } shape;
 * callers can override via the addDefaults prop for richer shapes.
 */
export function defaultAddDefaults(key) {
  return { key, label: autoLabel(key) };
}

/**
 * Default factory for operator entries. Shape: { display, wire } with
 * display === wire. This matches the Sprint 3.5 spec where users edit one
 * value that serves both roles, but the schema retains both fields so
 * advanced users can differentiate them later via JSON edit.
 */
export function defaultOperatorAddDefaults(value) {
  return { display: value, wire: value };
}

/**
 * True if `items` already contains an entry with this identifier value.
 */
export function hasKey(items, key, keyField = 'key') {
  return Array.isArray(items) && items.some(i => i && i[keyField] === key);
}

/**
 * Add an item to the list. String input goes through addDefaults; objects
 * are added as-is. Returns same array reference (no-op) if input is empty
 * or identifier already exists. Otherwise returns a new array.
 */
export function addItem(items, newItemOrKey, addDefaults = defaultAddDefaults, keyField = 'key') {
  const list = Array.isArray(items) ? items : [];
  const item = typeof newItemOrKey === 'string' ? addDefaults(newItemOrKey) : newItemOrKey;
  if (!item || !item[keyField]) return list;
  if (hasKey(list, item[keyField], keyField)) return list;
  return [...list, item];
}

/**
 * Remove an item by identifier value. Returns a new array.
 */
export function removeItem(items, key, keyField = 'key') {
  const list = Array.isArray(items) ? items : [];
  return list.filter(i => i && i[keyField] !== key);
}

/**
 * Merge auto-detected suggestions into existing items.
 * Per the Sprint 3 spec: skip identifiers that already exist (no duplicates,
 * no overwrite). Returns a new array.
 */
export function mergeSuggestions(items, suggestions, keyField = 'key') {
  const list = Array.isArray(items) ? items : [];
  if (!Array.isArray(suggestions)) return list;
  const existing = new Set(list.map(i => i?.[keyField]).filter(Boolean));
  const newOnes = suggestions.filter(s => s && s[keyField] && !existing.has(s[keyField]));
  return list.concat(newOnes);
}

/**
 * Apply user-edited JSON to replace the items list. Returns either:
 *   { ok: true,  items: [...] }    — parse succeeded, defensively cleaned
 *   { ok: false, error: string }   — parse failed
 * Cleaning rules: must be an array; each entry must have a string value
 * at `keyField`; duplicate identifiers are dropped (first occurrence wins).
 */
export function applyJsonToItems(jsonText, keyField = 'key') {
  let parsed;
  try {
    parsed = JSON.parse(jsonText);
  } catch (e) {
    return { ok: false, error: e.message || 'Invalid JSON' };
  }
  if (!Array.isArray(parsed)) {
    return { ok: false, error: 'Must be a JSON array' };
  }
  const seen = new Set();
  const clean = [];
  for (const p of parsed) {
    if (!p || typeof p[keyField] !== 'string') continue;
    if (seen.has(p[keyField])) continue;
    seen.add(p[keyField]);
    clean.push(p);
  }
  return { ok: true, items: clean };
}
